import type { FormField, FormSection } from '../types/formBuilder';
import { createNewField, createNewSection } from './formBuilderHelpers';

/** Stable key for onboarding answers (matches variable_name when set, else field id). */
export function getOnboardingAnswerKey(field: FormField): string {
  const v = field.variable_name?.trim();
  if (v) return v;
  return field.id;
}

export function defaultOnboardingSections(): FormSection[] {
  const section = createNewSection(0);
  section.title = 'Onboarding';
  const goal: FormField = {
    ...createNewField(),
    type: 'textarea',
    label: 'What do you want to achieve?',
    variable_name: 'goal',
    required: true,
    rows: 4,
  };
  section.fields = [goal];
  return [section];
}

export type NormalizeOnboardingOptions = {
  /** When `false`, do not inject defaults — return `[]` (admin + public). `undefined` means enabled. */
  onboardingFormEnabled?: boolean;
};

/**
 * Load from DB: new shape `{ sections }`, or legacy `OnboardField[]` array.
 */
export function normalizeOnboardingSections(
  raw: unknown,
  options?: NormalizeOnboardingOptions
): FormSection[] {
  if (options?.onboardingFormEnabled === false) {
    return [];
  }
  if (raw && typeof raw === 'object' && !Array.isArray(raw) && Array.isArray((raw as { sections?: unknown }).sections)) {
    const sections = (raw as { sections: FormSection[] }).sections;
    return sections.length > 0 ? sections : defaultOnboardingSections();
  }
  if (Array.isArray(raw)) {
    return [
      {
        id: `legacy-${Date.now()}`,
        title: 'Onboarding',
        fields: raw.map((f: Record<string, unknown>, i: number) => {
          const id = String(f.id ?? `field-${i}`);
          const type = f.type === 'textarea' ? 'textarea' : 'text';
          return {
            ...createNewField(),
            id,
            label: String(f.label ?? 'Field'),
            type,
            variable_name: id,
            required: !!f.required,
          } as FormField;
        }),
      },
    ];
  }
  return defaultOnboardingSections();
}

export function flattenOnboardingFields(sections: FormSection[]): FormField[] {
  return sections.flatMap((s) => s.fields);
}
